/**
 * ShareArrivalTracker
 *
 * Feb 2026 Analytics Commit B — `share.arrival` trigger.
 *
 * Behavior: fires `share.arrival` ONCE per browser session on mount
 * when the current URL carries a `utm_source` param. `/api/share/contest`
 * (and any future first-party share endpoint) already stamps the UTM
 * params on the returned share URL, so this component captures the
 * ARRIVAL side of the funnel without any additional server work.
 *
 * "Once per session" is enforced via sessionStorage — a user who
 * refreshes the page carrying the same UTM params should NOT fire
 * a second arrival event. sessionStorage persists until the tab
 * closes; localStorage would fire once per browser install (too
 * sticky), no-persistence would fire on every mount (double-count on
 * SPA route change back to /).
 *
 * Renders nothing. Mount once at app root, next to <AnalyticsPageView />.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

const SESSION_KEY = "pp:analytics:share_arrival_fired";

function readUtmParams(search) {
  const params = new URLSearchParams(search || "");
  const utm_source = params.get("utm_source") || null;
  const utm_medium = params.get("utm_medium") || null;
  const utm_campaign = params.get("utm_campaign") || null;
  return { utm_source, utm_medium, utm_campaign };
}

export default function ShareArrivalTracker() {
  const location = useLocation();

  useEffect(() => {
    // Fire once per session across route changes. Read the CURRENT
    // location.search — a user landing on /?utm_source=fb then SPA-
    // navigating to /pricing should NOT fire a second arrival.
    let already;
    try {
      already = window.sessionStorage.getItem(SESSION_KEY);
    } catch {
      // Storage disabled (Safari private mode etc). Skip — the
      // page.view beacon still captures the arrival's UTMs.
      return;
    }
    if (already) return;

    const { utm_source, utm_medium, utm_campaign } = readUtmParams(
      location.search,
    );
    if (!utm_source) return;

    try {
      window.sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      // Ignore — we'll still fire the event; worst case a double-fire
      // on a same-session UTM refresh is acceptable.
    }

    trackEvent("share.arrival", {
      utm_source,
      utm_medium,
      utm_campaign,
    });
    // Intentionally NOT depending on location.search — we only want
    // to fire once per session. The mount-only dep list is
    // deliberate. Route changes after mount trigger the effect but
    // the sessionStorage guard prevents duplicate emission.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
